import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { apiFetch } from '../utils/httpClient';
import { useAuth } from '../contexts/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/free-solid-svg-icons';

const PageContainer = styled.div`
  min-height: 100vh;
  min-height: 100dvh; /* Use dynamic viewport height for mobile */
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.9), rgba(204, 51, 51, 0.25));
  padding: clamp(var(--spacing-4), 4vw, var(--spacing-8));
  padding-top: max(clamp(var(--spacing-4), 4vw, var(--spacing-8)), env(safe-area-inset-top));
  padding-bottom: max(clamp(var(--spacing-4), 4vw, var(--spacing-8)), env(safe-area-inset-bottom));
  box-sizing: border-box;
  overflow-x: hidden;
  
  @media (max-width: 48rem) { /* --breakpoint-tablet */
    padding: var(--spacing-4);
    padding-top: max(var(--spacing-4), env(safe-area-inset-top));
    padding-bottom: max(var(--spacing-4), env(safe-area-inset-bottom));
  }
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    padding: var(--spacing-3);
    padding-top: max(var(--spacing-3), env(safe-area-inset-top));
    padding-bottom: max(var(--spacing-3), env(safe-area-inset-bottom));
  }
`;

const Card = styled.div`
  width: 100%;
  max-width: 28.75rem; /* 460px in rem */
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: clamp(var(--radius-lg), 2vw, 1rem);
  padding: clamp(var(--spacing-6), 4vw, var(--spacing-10));
  color: #fff;
  box-shadow: 0 20px 45px rgba(0, 0, 0, 0.45);
  text-align: center;
  box-sizing: border-box;
  
  @media (max-width: 48rem) { /* --breakpoint-tablet */
    max-width: 100%;
    padding: var(--spacing-6);
  }
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    padding: var(--spacing-5);
    border-radius: var(--radius-md);
  }
`;

const Title = styled.h1`
  font-size: clamp(var(--font-size-xl), 3vw, 1.75rem);
  margin-bottom: 0.5rem;
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    font-size: var(--font-size-lg);
  }
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.65);
  margin-bottom: 2rem;
`;

const SuccessMessage = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-3);
  padding: var(--spacing-4) var(--spacing-5);
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.12), rgba(5, 150, 105, 0.08));
  border: 1px solid rgba(16, 185, 129, 0.25);
  border-radius: var(--radius-lg);
  margin-bottom: var(--spacing-4);
  color: #6ee7b7; /* Lighter emerald for text */
  font-size: var(--font-size-sm);
  line-height: var(--line-height-relaxed);
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.15);
  backdrop-filter: blur(8px);
  transition: all 0.2s var(--transition-smooth);

  &:hover {
    border-color: rgba(16, 185, 129, 0.35);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
  }

  svg {
    flex-shrink: 0;
    margin-top: 0.125rem;
    color: #10b981; /* Emerald green for icon */
    font-size: 1.125rem;
  }
`;

const ErrorText = styled.div`
  background: rgba(255, 0, 0, 0.1);
  border: 1px solid rgba(255, 0, 0, 0.3);
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  color: #ff8080;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const Button = styled.button`
  margin-top: 1rem;
  padding: clamp(0.875rem, 2vw, 0.95rem) clamp(1.5rem, 4vw, 2rem);
  min-height: 2.75rem; /* 44px - accessibility standard for touch targets */
  border-radius: var(--radius-md);
  border: none;
  background: #cc3333;
  color: #fff;
  font-weight: 600;
  font-size: clamp(var(--font-size-sm), 2vw, 1rem);
  cursor: pointer;
  transition: background 0.2s ease;
  width: 100%;
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent; /* Remove tap highlight on mobile */

  &:hover {
    background: #b02b2b;
  }
  
  &:active:not(:disabled) {
    transform: scale(0.98); /* Touch feedback */
  }
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    font-size: 16px; /* Prevent zoom on iOS */
    min-height: 3rem; /* 48px - larger for easier tapping */
  }
`;

const LoadingText = styled.p`
  color: rgba(255, 255, 255, 0.75);
  font-size: 1rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
  text-align: left;
  margin-top: var(--spacing-4);
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
`;

const Label = styled.label`
  color: rgba(255, 255, 255, 0.9);
  font-size: var(--font-size-sm);
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: clamp(0.75rem, 2vw, 0.85rem) clamp(0.875rem, 2vw, 1rem);
  min-height: 2.75rem; /* 44px - accessibility standard for touch targets */
  border-radius: var(--radius-md);
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font-size: clamp(var(--font-size-sm), 2vw, 1rem);
  transition: border-color 0.2s ease, background 0.2s ease;
  box-sizing: border-box;
  -webkit-appearance: none;
  appearance: none;

  &:focus {
    outline: none;
    border-color: #cc3333;
    background: rgba(255, 255, 255, 0.08);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    font-size: 16px; /* Prevent zoom on iOS */
  }
`;

const PasswordHint = styled.p`
  color: rgba(255, 255, 255, 0.5);
  font-size: var(--font-size-xs);
  margin-top: calc(var(--spacing-1) * -1);
`;

interface VerifyEmailPageProps {
  onGoToLogin: () => void;
}

export const VerifyEmailPage: React.FC<VerifyEmailPageProps> = ({ onGoToLogin }) => {
  const { logout } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'password-setup'>('loading');
  const [message, setMessage] = useState<string>('');
  const [needsPassword, setNeedsPassword] = useState<boolean>(false);
  const [verificationToken, setVerificationToken] = useState<string>('');
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  const [settingPassword, setSettingPassword] = useState<boolean>(false);

  // Clear any existing session when on verification page (before verification)
  useEffect(() => {
    const clearSession = async () => {
      try {
        // Clear any existing authentication cookies and local state
        await logout();
        // Also clear the auth cookie directly via API call to ensure it's cleared
        try {
          await apiFetch('/api/v1/auth/logout', { method: 'POST' });
        } catch {
          // Ignore errors
        }
      } catch {
        // Ignore errors - just ensure we clear local state
      }
    };
    clearSession();
  }, [logout]);

  useEffect(() => {
    const verifyEmail = async () => {
      // Get token from URL query params
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      
      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link. No token provided.');
        return;
      }

      setVerificationToken(token);

      try {
        const response = await apiFetch(`/api/v1/auth/verify-email?token=${encodeURIComponent(token)}`, {
          method: 'GET',
        });

        if (!response.ok) {
          const data = await response.json().catch(() => null);
          const errorMessage = data?.detail || 'Verification failed. The link may be expired or invalid.';
          setStatus('error');
          setMessage(errorMessage);
          return;
        }

        const data = await response.json();
        
        // Ensure no session is active after verification
        try {
          await logout();
        } catch {
          // Ignore errors
        }
        
        // Check if password needs to be set
        if (data.needs_password) {
          setNeedsPassword(true);
          setStatus('password-setup');
          setMessage('Email verified successfully! Please create your password to continue.');
        } else {
          setStatus('success');
          setMessage(data.message || 'Email verified successfully!');
        // Redirect to login page after 3 seconds
        setTimeout(() => {
          onGoToLogin();
        }, 3000);
        }
      } catch (err) {
        setStatus('error');
        setMessage('An error occurred while verifying your email. Please try again.');
      }
    };

    verifyEmail();
  }, [onGoToLogin, logout]);

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    
    // Validation
    if (!username || !password || !confirmPassword) {
      setPasswordError('Please fill in all fields');
      return;
    }
    
    if (username.trim().length < 2) {
      setPasswordError('Username must be at least 2 characters long');
      return;
    }
    
    if (username.length > 50) {
      setPasswordError('Username cannot exceed 50 characters');
      return;
    }
    
    if (password.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return;
    }
    
    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    
    setSettingPassword(true);
    
    try {
      const response = await apiFetch('/api/v1/auth/set-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: verificationToken,
          username: username.trim(),
          password: password,
          confirm_password: confirmPassword,
        }),
      });
      
      if (!response.ok) {
        const data = await response.json().catch(() => null);
        const errorMessage = data?.detail || 'Failed to set password. Please try again.';
        setPasswordError(errorMessage);
        setSettingPassword(false);
        return;
      }
      
      const data = await response.json();
      setStatus('success');
      setMessage(data.message || 'Password set successfully! You can now log in.');
      
      // Redirect to login page after 2 seconds
      setTimeout(() => {
        onGoToLogin();
      }, 2000);
    } catch (err) {
      setPasswordError('An error occurred while setting your password. Please try again.');
      setSettingPassword(false);
    }
  };

  return (
    <PageContainer>
      <Card>
        <Title>Email Verification</Title>
        
        {status === 'loading' && (
          <>
            <LoadingText>Verifying your email...</LoadingText>
            <Subtitle>Please wait</Subtitle>
          </>
        )}

        {status === 'password-setup' && (
          <>
            <SuccessMessage>
              <FontAwesomeIcon icon={faCircleCheck} />
              <div>{message}</div>
            </SuccessMessage>
            <Form onSubmit={handleSetPassword}>
              <FormGroup>
                <Label htmlFor="username">Username</Label>
                <Input
                  type="text"
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Choose a username (2-50 characters)"
                  disabled={settingPassword}
                  required
                  minLength={2}
                  maxLength={50}
                  autoComplete="username"
                />
                <PasswordHint>This will be your display name</PasswordHint>
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="password">Password</Label>
                <Input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password (min 8 characters)"
                  disabled={settingPassword}
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
                <PasswordHint>Must be at least 8 characters long</PasswordHint>
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  disabled={settingPassword}
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
              </FormGroup>
              
              {passwordError && (
                <ErrorText>{passwordError}</ErrorText>
              )}
              
              <Button type="submit" disabled={settingPassword}>
                {settingPassword ? 'Setting Password...' : 'Set Password'}
              </Button>
            </Form>
          </>
        )}

        {status === 'success' && (
          <>
            <SuccessMessage>
              <FontAwesomeIcon icon={faCircleCheck} />
              <div>
                {message}
              <br />
              <br />
              Redirecting to login page...
              </div>
            </SuccessMessage>
            <Button onClick={onGoToLogin}>
              Go to Login
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <ErrorText>{message}</ErrorText>
            <Subtitle>Please check your email for a new verification link or contact support.</Subtitle>
            <Button onClick={onGoToLogin}>
              Go to Login
            </Button>
          </>
        )}
      </Card>
    </PageContainer>
  );
};

